import React, {useMemo} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {REGEX_LEADING_ZERO} from 'consts'
import {formatPhoneNumber} from 'utils'
import {Button, Modal, Paragraph} from 'common/components'
import {useSelector} from 'lib/redux'
import convertUnit from 'lib/unit'
import {GiftShopTemplateVerificationMethodItem} from '../../../template'
import {GiftShopEditProfilePhoneResendModalProps} from './GiftShopEditProfileModalProps'

const StyledModal = styled(Modal)`
  padding: ${convertUnit(20)};
  width: 100%;
  max-width: ${convertUnit(295)};
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${convertUnit(20)};
  text-align: center;
`

const StyledSelectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertUnit(8)};
`

const StyledButtonContainer = styled.div`
  display: flex;
  gap: ${convertUnit(20)};
`

const StyledButton = styled(Button)`
  width: 100%;
`

export default function GiftShopEditProfilePhoneResendModal({
  stateVerificationMethod,
  onCancel,
  onSelect,
  title,
}: GiftShopEditProfilePhoneResendModalProps) {
  const {translate} = useTranslation()
  const {phoneCode, phoneNumber} = useSelector('userNewPhone') || {}
  const [verificationMethod, setVerificationMethod] = stateVerificationMethod

  const displayedPhoneNumber = useMemo(
    () =>
      (phoneCode &&
        phoneNumber &&
        `+${phoneCode} ${formatPhoneNumber(
          phoneNumber.replace(REGEX_LEADING_ZERO, ''),
        )}`) ||
      '',
    [phoneCode, phoneNumber],
  )

  const handleRenderWhatsappSelection = useMemo(
    () => (
      <GiftShopTemplateVerificationMethodItem
        icon="whatsapp-ol"
        text={translate('giftShop:whatsappTo')}
        info={displayedPhoneNumber}
        selected={verificationMethod === 'whatsapp'}
        onClick={() => setVerificationMethod('whatsapp')}
      />
    ),
    [
      displayedPhoneNumber,
      setVerificationMethod,
      translate,
      verificationMethod,
    ],
  )

  const handleRenderTextSelection = useMemo(
    () => (
      <GiftShopTemplateVerificationMethodItem
        icon="message-ol"
        text={translate('giftShop:textTo')}
        info={displayedPhoneNumber}
        selected={verificationMethod === 'sms'}
        onClick={() => setVerificationMethod('sms')}
      />
    ),
    [
      displayedPhoneNumber,
      setVerificationMethod,
      translate,
      verificationMethod,
    ],
  )

  const handleRenderCancelBtn = useMemo(
    () => (
      <StyledButton
        label={translate('global:cancel')}
        color="primary_5"
        backgroundColor="white_2"
        backgroundHoverColor="white_3"
        onClick={onCancel}
      />
    ),
    [onCancel, translate],
  )

  const handleRenderSelectBtn = useMemo(
    () => (
      <StyledButton
        label={translate('global:select')}
        onClick={() => onSelect(verificationMethod)}
      />
    ),
    [onSelect, translate, verificationMethod],
  )

  return (
    <StyledModal visible onBackdropPress={onCancel}>
      <Paragraph fontSize="l" fontWeight="bold">
        {title || translate('giftShop:selectVerificationMethod')}
      </Paragraph>
      <StyledSelectionContainer>
        {handleRenderWhatsappSelection}
        {handleRenderTextSelection}
      </StyledSelectionContainer>
      <StyledButtonContainer>
        {handleRenderCancelBtn}
        {handleRenderSelectBtn}
      </StyledButtonContainer>
    </StyledModal>
  )
}
